import {
  AlertCircle,
  AlertTriangle,
  BarChart3,
  Brain,
  Calendar,
  ClipboardCheck,
  FileText,
  FolderKanban,
  GraduationCap,
  HardHat,
  Package,
  ShieldCheck,
  Sparkles,
  Users,
  Wrench,
  Clock,
} from 'lucide-react';

const DASHBOARD_PATH_BY_ROLE = {
  industrial: '/dashboard/industrial',
  industrialAdmin: '/dashboard/industrial',
  'industrial-admin': '/dashboard/industrial',
  executive: '/dashboard/executive',
};

const getDashboardPath = (role) => {
  return DASHBOARD_PATH_BY_ROLE[role] || '/dashboard';
};

const formatBadge = (value) => {
  if (!value && value !== 0) {
    return undefined;
  }

  if (typeof value === 'number') {
    if (value >= 1000) {
      const rounded = Math.round((value / 1000) * 10) / 10;
      return `${rounded}k`;
    }
    return `${value}`;
  }

  return value;
};

export function getStripeSidebarSections(role = 'sc', options = {}) {
  const { pendingProposalsCount } = options;
  const normalizedRole = role || 'sc';
  const dashboardPath = getDashboardPath(normalizedRole);

  const sections = [
    {
      title: 'Command Center',
      items: [
        {
          icon: Sparkles,
          label: 'Live Dashboard',
          path: dashboardPath,
          description: 'AI safety intelligence, automation coverage, and live telemetry',
          highlight: true,
          badge: 'Live',
          badgeTone: 'success',
        },
        {
          icon: FolderKanban,
          label: 'Projects',
          path: '/projects',
          description: 'Portfolio drilldowns across jobsites and programs',
        },
        {
          icon: FileText,
          label: 'Reports',
          path: '/reports',
          description: 'Compliance dossiers, incident retros, export-ready packets',
        },
        {
          icon: Calendar,
          label: 'Schedule',
          path: '/schedule',
          description: 'Inspections, training, and automation calendar',
        },
      ],
    },
    {
      title: 'Program Controls',
      items: [
        {
          icon: ShieldCheck,
          label: 'Compliance Hub',
          path: '/compliance',
          description: 'OSHA 1926 readiness, insurance requirements, corrective actions',
        },
      ],
    },
    {
      title: 'Compliance Data',
      items: [
        {
          icon: Wrench,
          label: 'Equipment Inventory',
          path: '/dashboard/equipment',
          description: 'Track construction equipment and inspection status',
        },
        {
          icon: Clock,
          label: 'Worker Hours',
          path: '/dashboard/worker-hours',
          description: 'Manual timesheet entry and TRIR calculations',
        },
        {
          icon: FileText,
          label: 'OSHA 300 Log',
          path: '/dashboard/osha-300',
          description: 'OSHA recordkeeping and safety metrics',
        },
        {
          icon: AlertTriangle,
          label: 'Incident Reports',
          path: '/incidents',
          description: 'Report and track safety incidents',
        },
      ],
    },
  ];

  return sections;
}

/**
 * Quick Action ID → route path mapping.
 * Single source of truth used by both QuickActionGrid and NavigationDropdown.
 */
export const QUICK_ACTION_ROUTES = {
  report: '/incidents',
  workers: '/workers',
  foreman: '/foreman',
  sites: '/projects',
  assets: '/assets',
  reports: '/reports',
  schedule: '/schedule',
  analytics: '/analytics',
  training: '/training',
};

/**
 * Returns all navigable page sections for the NavigationDropdown.
 * Superset of the sidebar sections — includes pages not shown in the sidebar.
 */
export function getNavigationDropdownSections(_role = 'sc') {
  const sections = [
    {
      title: 'Command Center',
      items: [
        { icon: Sparkles, label: 'Live Dashboard', path: '/command-center' },
        { icon: FolderKanban, label: 'Projects', path: '/projects' },
        { icon: FileText, label: 'Reports', path: '/reports' },
        { icon: Calendar, label: 'Schedule', path: '/schedule' },
        { icon: Users, label: 'Workers', path: '/workers' },
        { icon: HardHat, label: 'Foreman', path: '/foreman' },
        { icon: Package, label: 'Assets', path: '/assets' },
        { icon: BarChart3, label: 'Analytics', path: '/analytics' },
        { icon: GraduationCap, label: 'Training', path: '/training' },
        { icon: Brain, label: 'AI Intelligence', path: '/ai-intelligence' },
      ],
    },
    {
      title: 'Compliance Data',
      items: [
        { icon: ShieldCheck, label: 'Compliance Hub', path: '/compliance' },
        { icon: Wrench, label: 'Equipment Inventory', path: '/dashboard/equipment' },
        { icon: Clock, label: 'Worker Hours', path: '/dashboard/worker-hours' },
        { icon: FileText, label: 'OSHA 300 Log', path: '/dashboard/osha-300' },
        { icon: AlertTriangle, label: 'Incident Reports', path: '/incidents' },
      ],
    },
    {
      title: 'Safety & Reporting',
      items: [
        { icon: ClipboardCheck, label: 'Safety Assessment', path: '/safety-assessment' },
        { icon: AlertCircle, label: 'Hazard Report', path: '/hazard-report' },
      ],
    },
  ];

  return sections;
}
